import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { educationGuideContents, educationGuides, guideDateLabel, guides, type EducationGuideContent, type EducationGuideSlug } from '../../../packages/shared/src/guides.js';
import {
  concentrationBand, concentrationBands, concentrationMax, defaultWearChoice, oudStrength, oudStyleMatches, oudStyles,
  unfoldHours, unfoldLayers, unfoldMoments, unfoldProminence, wearCurves, wearFactors, wearHours,
  type OudFragrance, type OudStyleId, type WearChoice,
} from '../../../packages/shared/src/guide-features.js';
import { api } from './api.js';
import { GuideTrail, useActiveSection } from './Guides.js';
import './education-guides.css';

const features: Record<EducationGuideSlug, (props: { oudFragrances?: OudFragrance[] }) => ReactNode> = {
  'perfume-notes-explained': () => <NotesUnfold />,
  'extrait-de-parfum': () => <ConcentrationScale />,
  'what-is-oud': ({ oudFragrances }) => <OudStyles initial={oudFragrances} />,
  'make-perfume-last-longer': () => <WearSketch />,
};

export function EducationGuide({ slug, oudFragrances }: { slug: EducationGuideSlug; oudFragrances?: OudFragrance[] }) {
  const guide = guides.find(item => item.slug === slug)!;
  const content: EducationGuideContent = educationGuides[slug];
  const contents = [...educationGuideContents(slug), { id: 'questions', title: 'Common questions' }];
  const active = useActiveSection(contents.map(item => item.id));
  const Feature = features[slug];
  return <article className="guides-page guide-article education-guide">
    <GuideTrail article current={guide.title} />
    <header className="guides-heading guide-article-heading">
      <div className="guide-heading-main"><h1>{guide.title}</h1><time className="guide-date" dateTime={guide.publishedAt}>{guideDateLabel(guide.publishedAt)}</time></div>
      <div className="guide-standfirst"><p>{guide.description}</p></div>
    </header>
    <div className="guide-layout">
      <nav className="guide-contents" aria-label="In this guide">
        <h2>In this guide</h2>
        {contents.map(item => <a key={item.id} href={`#${item.id}`} aria-current={active === item.id ? 'true' : undefined}><span>{item.title}</span></a>)}
      </nav>
      <div className="guide-body education-guide-body">
        {content.sections.map(section => <Fragment key={section.id}>
          <section id={section.id} className="education-section" aria-labelledby={`${section.id}-title`}>
            <h2 id={`${section.id}-title`}>{section.heading}</h2>
            {section.paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
          </section>
          {section.id === content.feature.after && <section id={content.feature.id} className="guide-instrument" aria-labelledby={`${content.feature.id}-title`}>
            <Feature oudFragrances={oudFragrances} />
          </section>}
        </Fragment>)}
        <section id="questions" className="education-section education-questions" aria-labelledby="questions-title">
          <h2 id="questions-title">Common questions</h2>
          <dl>{content.faqs.map(faq => <div key={faq.question}><dt>{faq.question}</dt><dd>{faq.answer}</dd></div>)}</dl>
          <p className="education-next"><Link className="text-link" to="/finder">Find fragrances by scent</Link><Link className="text-link" to="/guides">More guides</Link></p>
        </section>
      </div>
    </div>
  </article>;
}

function InstrumentHead({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return <header className="instrument-head"><h2 id={`${id}-title`}>{title}</h2><p>{children}</p></header>;
}

/** A row of pill radio buttons; native radios keep arrow-key movement and form semantics. */
function Choice<T extends string>({ legend, name, value, options, onChange }: {
  legend: string; name: string; value: T; options: readonly { id: T; name: string; hint?: ReactNode }[]; onChange: (value: T) => void;
}) {
  return <fieldset className="instrument-choice">
    <legend>{legend}</legend>
    <div>{options.map(option => <label key={option.id} className={option.id === value ? 'is-selected' : undefined}>
      <input type="radio" name={name} value={option.id} checked={option.id === value} onChange={() => onChange(option.id)} />
      <span>{option.name}</span>{option.hint !== undefined && <small>{option.hint}</small>}
    </label>)}</div>
  </fieldset>;
}

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** SVG path for a curve sampled across the chart; y is 0–1, drawn upward. */
function curvePath(curve: (x: number) => number, width: number, height: number, area = false, samples = 120) {
  const points = Array.from({ length: samples + 1 }, (_, index) => {
    const x = index / samples;
    return `${(x * width).toFixed(1)},${(height - Math.max(0, Math.min(1, curve(x))) * height).toFixed(1)}`;
  });
  return area ? `M0,${height} L${points.join(' L')} L${width},${height} Z` : `M${points.join(' L')}`;
}

// ── Perfume notes explained: a scrubber through the life of one illustrative fragrance ──
const timeAt = (position: number) => unfoldHours * position * position;
const positionAt = (hours: number) => Math.sqrt(hours / unfoldHours);
function durationLabel(hours: number) {
  if (hours < 0.04) return 'Just sprayed';
  if (hours < 1) return `${Math.max(5, Math.round(hours * 60 / 5) * 5)} minutes`;
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded} ${rounded === 1 ? 'hour' : 'hours'}`;
}

function NotesUnfold() {
  const [position, setPosition] = useState(positionAt(0.25));
  const [playing, setPlaying] = useState(false);
  const frame = useRef(0);
  const hours = timeAt(position);
  const moment = [...unfoldMoments].reverse().find(item => hours >= item.hours - 0.02) ?? unfoldMoments[0];
  const levels = unfoldLayers.map(layer => ({ ...layer, level: unfoldProminence(layer.id, hours) }));
  const leading = levels.reduce((best, layer) => layer.level > best.level ? layer : best);
  useEffect(() => {
    if (!playing) return;
    if (prefersReducedMotion()) {
      // Step through the moments instead of sweeping.
      const timer = window.setInterval(() => setPosition(current => {
        const next = unfoldMoments.find(item => positionAt(item.hours) > current + 0.001);
        if (!next) { setPlaying(false); return current; }
        return positionAt(next.hours);
      }), 1600);
      return () => window.clearInterval(timer);
    }
    let last = performance.now();
    const tick = (now: number) => {
      const step = (now - last) / 9000;
      last = now;
      setPosition(current => {
        if (current + step >= 1) { setPlaying(false); return 1; }
        return current + step;
      });
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [playing]);
  const play = () => { if (position >= 0.999) setPosition(0); setPlaying(value => !value); };
  return <div className="notes-unfold">
    <InstrumentHead id="watch-it-unfold" title="Watch a fragrance unfold">Drag through the day to see how the top, heart and base of one fragrance trade places.</InstrumentHead>
    <div className="unfold-stage" style={{ ['--pos' as string]: position.toFixed(4) }}>
      {levels.map(layer => <div key={layer.id} className={layer.id === leading.id ? 'unfold-ridge is-leading' : 'unfold-ridge'}>
        <span className="unfold-ridge-name">{layer.name}</span>
        <svg viewBox="0 0 600 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path className="unfold-area" d={curvePath(x => unfoldProminence(layer.id, timeAt(x)), 600, 60, true)} />
          <path className="unfold-line" d={curvePath(x => unfoldProminence(layer.id, timeAt(x)), 600, 60)} vectorEffect="non-scaling-stroke" />
        </svg>
      </div>)}
      <span className="unfold-cursor" aria-hidden="true" />
      <div className="unfold-ticks" aria-hidden="true">{unfoldMoments.map(item => <span key={item.label} style={{ ['--at' as string]: positionAt(item.hours).toFixed(4) }}>{item.label}</span>)}</div>
      <div className="unfold-controls">
        <button type="button" className="instrument-play" onClick={play} aria-pressed={playing}>
          {playing ? <Pause size={15} aria-hidden /> : <Play size={15} aria-hidden />}<span>{playing ? 'Pause' : position >= 0.999 ? 'Replay' : 'Play'}</span>
        </button>
        <label className="unfold-range">
          <span className="sr-only">Time since spraying</span>
          <input type="range" min={0} max={1} step={0.005} value={position} aria-valuetext={durationLabel(hours)}
            onChange={event => { setPlaying(false); setPosition(Number(event.target.value)); }} />
        </label>
      </div>
    </div>
    <div className="unfold-readout">
      <div className="unfold-moment"><strong>{durationLabel(hours)}</strong><p aria-live="polite">{moment.line}</p></div>
      <dl className="unfold-levels">{levels.map(layer => <div key={layer.id} style={{ ['--level' as string]: layer.level.toFixed(3) }}>
        <dt>{layer.name}</dt>
        <dd><span className="unfold-meter" aria-hidden="true"><i /></span><span className="unfold-notes">{layer.notes.join(' · ')}</span></dd>
      </div>)}</dl>
    </div>
    <p className="instrument-note">An illustrative fragrance. Real timings vary with the formula, your skin and how much you apply.</p>
  </div>;
}

// ── Extrait, EDP and EDT: a slider across the commonly quoted concentration ranges ──
function ConcentrationScale() {
  const [percent, setPercent] = useState(18);
  const band = concentrationBand(percent);
  const id = useId();
  return <div className="concentration-scale">
    <InstrumentHead id="concentration-scale" title="The concentration scale">Slide to change the share of aromatic materials in the bottle and see which label it usually earns.</InstrumentHead>
    <div className="concentration-stage">
      <figure className="concentration-vial" aria-hidden="true" style={{ ['--fill' as string]: `${percent / 100}` }}>
        <div className="vial-cap" />
        <div className="vial-glass"><div className="vial-liquid"><div className="vial-oil" /></div></div>
        <figcaption><span>{percent}%</span> aromatic materials</figcaption>
      </figure>
      <div className="concentration-main">
        <div className="concentration-reading" aria-live="polite">
          <strong>{band.name}</strong>
          <span>Often quoted as about {band.from}–{band.to}%</span>
          <p>{band.line}</p>
        </div>
        <div className="concentration-ruler">
          <label htmlFor={id} className="sr-only">Share of aromatic materials</label>
          <input id={id} type="range" min={1} max={concentrationMax} step={1} value={percent} aria-valuetext={`${percent}%, ${band.name}`} onChange={event => setPercent(Number(event.target.value))} />
          <div className="concentration-bands" aria-hidden="true">{concentrationBands.map(item =>
            <span key={item.id} className={item.id === band.id ? 'is-current' : undefined} style={{ left: `${(item.from / concentrationMax) * 100}%`, width: `${((item.to - item.from) / concentrationMax) * 100}%` }} />)}</div>
          <div className="concentration-axis" aria-hidden="true">{[0, 5, 15, 20, 40].map(value => <span key={value} style={{ left: `${(value / concentrationMax) * 100}%` }}>{value}%</span>)}</div>
        </div>
        <Choice legend="Jump to a label" name={`${id}-label`} value={band.id} options={concentrationBands.map(item => ({ id: item.id, name: item.short }))}
          onChange={value => { const next = concentrationBands.find(item => item.id === value)!; setPercent(Math.round((next.from + Math.min(next.to, 30)) / 2)); }} />
      </div>
    </div>
    <p className="instrument-note">Ranges commonly quoted for each label. Brands set their own formulas, and there is no single standard every house follows.</p>
  </div>;
}

// ── What is oud?: the collection's oud fragrances, sorted by what they pair oud with ──
function Dots({ value, label }: { value: number; label: string }) {
  return <span className="oud-dots" role="img" aria-label={`${label}: ${value} of 5`}>{[1, 2, 3, 4, 5].map(step => <i key={step} className={step <= value ? 'is-on' : undefined} />)}</span>;
}

function OudStyles({ initial }: { initial?: OudFragrance[] }) {
  const [items, setItems] = useState<OudFragrance[] | undefined>(initial);
  const [failed, setFailed] = useState(false);
  const [style, setStyle] = useState<OudStyleId>('rose');
  const [expanded, setExpanded] = useState(false);
  const [settle, setSettle] = useState(false);
  const load = () => {
    setFailed(false);
    api<OudFragrance[]>('/api/v1/guides/what-is-oud/fragrances').then(setItems, () => setFailed(true));
  };
  useEffect(() => { if (!initial) load(); }, []);
  const current = oudStyles.find(row => row.id === style)!;
  const matches = items ? oudStyleMatches(items, style) : [];
  const shown = expanded ? matches : matches.slice(0, 6);
  const choose = (next: OudStyleId) => { setStyle(next); setExpanded(false); setSettle(true); };
  const pairingName = current.name.replace(/ & oud$/, '').split(' & ')[0];
  return <div className="oud-styles">
    <InstrumentHead id="oud-styles" title="Oud styles in the collection">Choose a pairing to see the oud fragrances in our collection that lean that way.</InstrumentHead>
    <Choice legend="Oud paired with" name="oud-style" value={style}
      options={oudStyles.map(row => ({ id: row.id, name: row.name, hint: items ? oudStyleMatches(items, row.id).length : undefined }))} onChange={choose} />
    <div className="oud-current">
      <p>{current.line}</p>
      {items && <p className="oud-count" aria-live="polite">{matches.length} {matches.length === 1 ? 'fragrance' : 'fragrances'}</p>}
    </div>
    {!items && !failed && <p className="instrument-status" role="status">Loading oud fragrances from the collection…</p>}
    {failed && <p className="instrument-status" role="alert">The collection could not be loaded. <button type="button" className="text-link" onClick={load}>Try again</button></p>}
    {items && matches.length === 0 && <p className="instrument-status">No fragrances in the collection have this pairing recorded yet.</p>}
    {items && matches.length > 0 && <ol className={settle ? 'oud-list oud-list--settle' : 'oud-list'} key={style}>
      {shown.map(({ item, pairing, oud }) => <li key={item.slug}>
        <Link to={`/fragrances/${item.slug}`} className="oud-row">
          <span className="oud-row-name"><small>{item.brand?.name}</small><strong>{item.name}</strong><em>{item.concentration}</em></span>
          <span className="oud-row-scale"><span>Oud</span><Dots value={oud} label="Oud" /></span>
          <span className="oud-row-scale"><span>{pairingName}</span><Dots value={pairing} label={pairingName} /></span>
        </Link>
      </li>)}
    </ol>}
    <div className="oud-foot">
      {matches.length > 6 && <button type="button" className="text-link" aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>{expanded ? 'Show fewer' : `Show all ${matches.length}`}</button>}
      <Link className="text-link" to="/finder?notes=oud">Browse every oud fragrance in the finder</Link>
    </div>
    <p className="instrument-note">Sorted by how strongly each pairing is recorded. Strengths are our editorial accord estimates on a 0–5 scale{items ? `, across ${items.filter(item => oudStrength(item) > 0).length} oud fragrances` : ''}.</p>
  </div>;
}

// ── How to make perfume last longer: what others notice versus what you notice ──
function WearSketch() {
  const [choice, setChoice] = useState<WearChoice>(defaultWearChoice);
  const curves = wearCurves(choice);
  const x = (hours: number) => `${(hours / wearHours) * 100}%`;
  const others = (position: number) => curves.others(position * wearHours);
  const self = (position: number) => curves.self(position * wearHours);
  const gap = `${curvePath(others, 600, 220)} L${curvePath(self, 600, 220).slice(1).split(' L').reverse().join(' L')} Z`;
  const threshold = 220 - curves.noticeable * 220;
  return <div className="wear-sketch">
    <InstrumentHead id="wear-sketch" title="Sketch your wear">Change the fragrance and conditions. The gap between the two lines is the scent you stop noticing while it is still there.</InstrumentHead>
    <div className="wear-controls">{wearFactors.map(factor =>
      <Choice key={factor.id} legend={factor.name} name={`wear-${factor.id}`} value={choice[factor.id]} options={factor.options}
        onChange={value => setChoice(current => ({ ...current, [factor.id]: value }))} />)}</div>
    <figure className="wear-chart">
      <div className="wear-plot">
        <svg viewBox="0 0 600 220" preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path className="wear-gap" d={gap} />
          <line className="wear-threshold" x1="0" x2="600" y1={threshold} y2={threshold} vectorEffect="non-scaling-stroke" />
          <path className="wear-others" d={curvePath(others, 600, 220)} vectorEffect="non-scaling-stroke" />
          <path className="wear-self" d={curvePath(self, 600, 220)} vectorEffect="non-scaling-stroke" />
        </svg>
        <span className="wear-threshold-label" style={{ top: `${(threshold / 220) * 100}%` }}>Hard to notice</span>
        <span className="wear-marker wear-marker--self" style={{ left: x(curves.selfFade) }}>You stop noticing</span>
        {curves.othersFade < wearHours
          ? <span className="wear-marker wear-marker--others" style={{ left: x(curves.othersFade) }}>Others stop noticing</span>
          : <span className="wear-marker wear-marker--others wear-marker--end" style={{ left: '100%' }}>Still there tonight</span>}
      </div>
      <div className="wear-axis" aria-hidden="true"><span>Spray</span><span>Midday</span><span>Evening</span></div>
      <figcaption className="wear-key"><span className="wear-key-others">Noticeable to people near you</span><span className="wear-key-self">Noticeable to you</span></figcaption>
    </figure>
    <ul className="wear-reasons" aria-live="polite">{curves.lines.map(line => <li key={line}>{line}</li>)}</ul>
    <p className="instrument-note">A sketch of direction, not a measurement. The fragrance itself matters most; these conditions nudge it.</p>
  </div>;
}
